import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";
import { RainbowMark } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TABS = [
  { key: "intakes", label: "Family Intakes" },
  { key: "referrals", label: "Provider Referrals" },
  { key: "applications", label: "Job Applications" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const Field = ({ label, value }) =>
  value ? (
    <div>
      <span className="text-xs uppercase tracking-wider text-brand-muted font-semibold">{label}</span>
      <p className="text-sm text-brand-ink mt-0.5 whitespace-pre-wrap break-words">{value}</p>
    </div>
  ) : null;

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("intakes");
  const [data, setData] = useState({ intakes: [], referrals: [], applications: [] });
  usePageMeta("Admin | Milestone Pediatric Therapy", "");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const load = useCallback(async (retried = false) => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/intakes"),
        api.get("/admin/referrals"),
        api.get("/admin/applications"),
      ]);
      setData({ intakes: a.data, referrals: b.data, applications: c.data });
    } catch (e) {
      if (e?.response?.status === 401 && !retried) {
        try {
          await api.post("/auth/refresh");
          return load(true);
        } catch {
          setUser(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#F4F7FD] flex items-center justify-center" data-testid="admin-loading">
        <span className="text-brand-muted text-sm">Loading…</span>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#F4F7FD] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-lift p-10"
        >
          <div className="flex items-center gap-3 mb-8">
            <RainbowMark className="h-7 w-11" />
            <span className="leading-none">
              <span className="block font-serif text-xl font-semibold text-brand-blue">Milestone</span>
              <span className="block text-[10px] uppercase tracking-[0.3em] text-brand-coral font-semibold mt-0.5">
                Admin
              </span>
            </span>
          </div>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-brand-coral font-medium">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="w-full rounded-full bg-brand-blue text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-brand-deep disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <Link to="/" data-testid="admin-back-link" className="block text-center text-sm text-brand-muted hover:text-brand-blue transition-colors duration-300">
              Back to site
            </Link>
          </div>
        </form>
      </div>
    );
  }

  const rows = data[tab];

  return (
    <div className="min-h-screen bg-[#F4F7FD]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-brand-lightblue/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RainbowMark className="h-7 w-11" />
            <span className="font-serif text-xl font-semibold text-brand-blue">Milestone Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load()}
              className="inline-flex items-center gap-2 rounded-full border border-brand-blue/30 text-brand-blue text-sm font-semibold px-5 py-2.5 transition-colors duration-300 hover:bg-brand-pale"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-brand-coral text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-300 hover:bg-[#f06666]"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap gap-3 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-6 py-3 text-sm font-semibold transition-[background-color,color,box-shadow] duration-300 ${
                tab === t.key
                  ? "bg-brand-blue text-white shadow-soft"
                  : "bg-white text-brand-muted border border-brand-lightblue/50 hover:text-brand-blue"
              }`}
            >
              {t.label}
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${tab === t.key ? "bg-white/20" : "bg-brand-pale"}`}>
                {data[t.key].length}
              </span>
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <div data-testid="admin-empty-state" className="rounded-3xl bg-white border border-brand-lightblue/40 shadow-soft p-16 text-center text-brand-muted">
            No submissions yet.
          </div>
        ) : (
          <div className="grid gap-5" data-testid={`admin-list-${tab}`}>
            {rows.map((r) => (
              <article key={r.id} data-testid={`admin-row-${r.id}`} className="rounded-3xl bg-white border border-brand-lightblue/40 shadow-soft p-7">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                  <div>
                    <h3 className="font-serif text-2xl font-semibold text-brand-blue">
                      {tab === "intakes" && (r.parent_first_name ? `${r.parent_first_name} ${r.parent_last_name || ""}` : r.parent_name)}
                      {tab === "referrals" && r.practice}
                      {tab === "applications" && r.name}
                    </h3>
                    <p className="text-xs text-brand-muted mt-1">{fmtDate(r.created_at)}</p>
                  </div>
                  {((tab === "applications" && r.resume_path) || (tab === "referrals" && r.doc_path)) && (
                    <a
                      href={`${API}/files/${tab === "applications" ? r.resume_path : r.doc_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`doc-download-${r.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-pale text-brand-blue text-sm font-semibold px-5 py-2.5 transition-colors duration-300 hover:bg-brand-lightblue/50"
                    >
                      <Download className="h-4 w-4" /> {r.resume_filename || r.doc_filename || "Document"}
                    </a>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tab === "intakes" && (
                    <>
                      <Field label="Parent" value={`${r.parent_first_name || ""} ${r.parent_last_name || ""}`.trim() || r.parent_name} />
                      <Field label="Email" value={r.email} />
                      <Field label="Phone" value={r.phone} />
                      <Field label="Preferred Contact" value={r.preferred_contact} />
                      <Field label="Child" value={`${r.child_first_name || ""} ${r.child_last_name || ""}`.trim()} />
                      <Field label="Date of Birth" value={r.child_dob} />
                      <Field label="Child Age" value={r.child_age} />
                      <Field label="Diagnosis" value={r.diagnosis} />
                      <Field label="School / Daycare" value={r.school} />
                      <Field label="Insurance Provider" value={r.insurance_provider} />
                      <Field label="Member ID" value={r.member_id} />
                      <Field label="Secondary Insurance" value={r.secondary_insurance} />
                      <Field label="Service Needs" value={r.service_needs} />
                      <Field label="Preferred Schedule" value={r.preferred_schedule} />
                      <Field label="Goals / Concerns" value={r.goals || r.message} />
                      <Field label="How They Heard" value={r.referral_source} />
                    </>
                  )}
                  {tab === "referrals" && (
                    <>
                      <Field label="Contact Name" value={r.contact_name} />
                      <Field label="Professional Role" value={r.role} />
                      <Field label="Email" value={r.email} />
                      <Field label="Phone" value={r.phone} />
                      <Field label="Reason for Referral" value={r.reason} />
                      <Field label="Patient First Name" value={r.patient_first_name} />
                      <Field label="Patient Age" value={r.patient_age} />
                      <Field label="Message" value={r.message} />
                    </>
                  )}
                  {tab === "applications" && (
                    <>
                      <Field label="Role of Interest" value={r.role} />
                      <Field label="Email" value={r.email} />
                      <Field label="Phone" value={r.phone} />
                      <Field label="Message" value={r.message} />
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
